import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import {
  setValidationAcknowledgements,
  useReviewCreationData,
  useValidationAcknowledgements,
} from 'reducers/reviewCreationSlice';

import { GradeMissing } from 'assets/assetsV1/iconComponents';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TagOutlined } from 'uiToolkitV1/Tag/TagOutlined';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { Employee, useGetGradeMissing } from '../../Hooks/dataHooks/validationDataHooks';
import { SkeletonLoaderMultiViewVariant } from '../../components/SkeletonLoaderReviewCreation';

const EmployeeRenderer: React.FC<ICellRendererParams<Employee>> = ({ data }) => (
  <ListItemComp
    title={data.displayName}
    subTitle={data.email}
    LeftComponent={() => <Avatar src={data.profilePhoto} name={data.displayName} />}
    RightComponent={null}
    subSubtitle={null}
    onClick={() => {}}
    isDisabled={false}
    active={false}
    withoutBackground
  />
);

const ViewsRenderer: React.FC<ICellRendererParams<Employee>> = ({ data }) => {
  if (!data.cohortName) return null;

  return (
    <TagOutlined StartIcon={null} startAvatarSrc={null} onClick={null}>
      {data.cohortName}
    </TagOutlined>
  );
};

const PAGE_SIZES = [5, 10, 20];
const ROW_HEIGHT = 68;
const ROW_HEADER_HEIGHT = 40;

export const GradeMissingValidation = () => {
  const { reviewCycleId } = useReviewCreationData();
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageNumber, setPageNumber] = useState(0);
  const gridRef = useRef<AgGridReact>();
  const { data: rowData, isLoading, isSuccess } = useGetGradeMissing({ reviewCycleId });

  const dispatch = useDispatch();
  const validations = useValidationAcknowledgements();

  useEffect(() => {
    if (rowData.length > 0) {
      dispatch(setValidationAcknowledgements({ key: 'isGradesMissingAcknowledged', value: false }));
    }
  }, [dispatch, rowData.length]);

  const gridStyle = useMemo(() => {
    const noOfRowsOnLastPage = rowData.length - pageSize * pageNumber;
    return {
      height: `calc(${ROW_HEIGHT + 1}px * ${Math.min(pageSize, noOfRowsOnLastPage)} + ${ROW_HEADER_HEIGHT}px)`,
      width: '100%',
    };
  }, [pageNumber, pageSize, rowData.length]);

  const columnDefs = useMemo<ColDef<Employee>[]>(
    () => [
      {
        headerName: 'Employee',
        cellRenderer: EmployeeRenderer,
        valueGetter: ({ data }) => data.displayName,
      },
      {
        headerName: 'Department',
        field: 'department',
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'Designation',
        field: 'designation',
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'View',
        cellRenderer: ViewsRenderer,
        valueGetter: ({ data }) => data.cohortName,
        filter: 'agSetColumnFilter',
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      headerComponent: AgGridHeader,
      headerComponentParams: {
        enableSorting: true,
        enableMenu: true,
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  if (isLoading) {
    return <SkeletonLoaderMultiViewVariant />;
  }

  // don't show this section if no pending validations
  if (rowData.length === 0) {
    return null;
  }

  const onPageChange = (_, page: number) => {
    setPageNumber(page);
    gridRef.current.api.paginationGoToPage(page);
  };
  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
  };

  const handleCheck = (e) => {
    dispatch(
      setValidationAcknowledgements({
        key: 'isGradesMissingAcknowledged',
        value: e.target.checked,
      })
    );
  };

  const gridStatePersistenceKey = null;

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title="Grade Missing"
        subTitle="These employees will not be able to nominate peers above and below them in the organization hierarchy (If peer review is applicable)"
        leftIconComponent={<GradeMissing boxSize="24px" color="icon.g2" />}
        rightComponent={
          <TextBodyMedium bold color="functional.C62C02" mr={3}>
            {rowData.length} Employees
          </TextBodyMedium>
        }
      />
      <ExpandablePaperBody p="8px 8px 0">
        <div style={gridStyle} className="ag-theme-alpine">
          <AgGridWrapper
            gridStatePersistenceKey={gridStatePersistenceKey}
            useFilterModelPersistence={false}
            useColumnStatePersistence={false}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            paginationPageSize={pageSize}
            pagination
            suppressPaginationPanel
            suppressScrollOnNewData
            rowHeight={ROW_HEIGHT}
            headerHeight={ROW_HEADER_HEIGHT}
            rowStyle={{ cursor: 'default' }}
          />
        </div>
        <Flex alignItems="center" justifyContent="space-between" m="8px 8px 0">
          <Checkbox isChecked={validations.isGradesMissingAcknowledged} onChange={handleCheck}>
            I understand that these employees will not be able to nominate peers above and below them in the
            organization hierarchy (If peer review is applicable)
          </Checkbox>
          <TablePagination
            count={rowData.length}
            onPageChange={onPageChange}
            rowsPerPage={pageSize}
            page={pageNumber}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZES}
          />
        </Flex>
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
